import AsyncStorage from '@react-native-async-storage/async-storage';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getLocalTime } from '../../../common/utils/time/time';

/**
 * Device-local "skip this guided step" flags, keyed per docket and scoped to
 * one run + service date so they reset naturally on a new day. Skips only
 * affect the My day board on this device; they never change server data.
 */
const useMyDayStepSkips = ({ prefix, serviceRunId, planned, timezone, disabled = false }) => {
	const storageKey = useMemo(() => {
		if (disabled || !serviceRunId || !planned) {
			return null;
		}

		const plannedServiceDate = getLocalTime(timezone, planned).format('YYYY-MM-DD');
		return `${prefix}_${serviceRunId}_${plannedServiceDate}`;
	}, [disabled, planned, prefix, serviceRunId, timezone]);

	const [skippedByDocketId, setSkippedByDocketId] = useState({});
	const [isLoading, setIsLoading] = useState(true);

	useEffect(() => {
		if (!storageKey) {
			setSkippedByDocketId({});
			setIsLoading(false);
			return;
		}

		let isActive = true;
		setIsLoading(true);

		(async () => {
			try {
				const storedValue = await AsyncStorage.getItem(storageKey);
				const parsedValue = storedValue ? JSON.parse(storedValue) : {};
				if (isActive) {
					setSkippedByDocketId(parsedValue || {});
				}
			}
			catch (error) {
				if (isActive) {
					setSkippedByDocketId({});
				}
			}
			finally {
				if (isActive) {
					setIsLoading(false);
				}
			}
		})();

		return () => {
			isActive = false;
		};
	}, [storageKey]);

	const updateSkips = useCallback((updater) => {
		if (disabled) {
			return;
		}

		setSkippedByDocketId((previousValue) => {
			const nextValue = typeof updater === 'function' ? updater(previousValue) : updater;

			if (storageKey) {
				void AsyncStorage.setItem(storageKey, JSON.stringify(nextValue));
			}

			return nextValue;
		});
	}, [disabled, storageKey]);

	const skipDocket = useCallback((docketId) => {
		if (!docketId) {
			return;
		}

		updateSkips(previousValue => ({
			...previousValue,
			[docketId]: true,
		}));
	}, [updateSkips]);

	const clearDocket = useCallback((docketId) => {
		if (!docketId) {
			return;
		}

		updateSkips((previousValue) => {
			if (!previousValue[docketId]) {
				return previousValue;
			}

			const nextValue = { ...previousValue };
			delete nextValue[docketId];
			return nextValue;
		});
	}, [updateSkips]);

	return {
		skippedByDocketId,
		skipDocket,
		clearDocket,
		isLoading,
	};
};

export default useMyDayStepSkips;
